import type { CSSProperties, ReactNode } from 'react';
import { Card, makeStyles, tokens } from '@fluentui/react-components';

interface Props {
  label: string;
  value: string | number;
  sub?: string;
  /** @deprecated usa `tone="danger"` — se mantiene por compatibilidad con usos existentes */
  accent?: boolean;
  /** Tono del valor principal. 'danger' reemplaza al antiguo `accent` boolean. */
  tone?: 'default' | 'danger';
  /** Ícono opcional (elemento de @fluentui/react-icons) en un chip circular arriba del label. */
  icon?: ReactNode;
  iconBg?: string;
  iconColor?: string;
  /** Delta vs. plan/periodo anterior, ej. "+8.0%". Se muestra junto a `arrow`. */
  delta?: string;
  /** '▲' | '▼' | '—' — o cualquier glifo corto. */
  arrow?: string;
  /** Color del delta+arrow; por defecto usa colores semánticos success/danger/neutral. */
  deltaTone?: 'success' | 'danger' | 'neutral';
}

const useStyles = makeStyles({
  card: {
    padding: '20px 24px',
    flex: '1',
    minWidth: '160px',
  },
  iconChip: {
    width: '30px',
    height: '30px',
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '12px',
  },
  label: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
  value: {
    fontSize: '28px',
    fontWeight: '700',
    lineHeight: '1',
  },
  sub: {
    marginTop: '4px',
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  deltaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    marginTop: '6px',
    fontSize: '12px',
  },
});

const DELTA_TONE_COLOR: Record<NonNullable<Props['deltaTone']>, string> = {
  success: tokens.colorPaletteGreenForeground1,
  danger: tokens.colorStatusDangerForeground1,
  neutral: tokens.colorNeutralForeground3,
};

/**
 * Tamaño de letra del valor principal.
 * - Números y montos ("MX$183,536.20", "21.8%"): una sola línea; la letra se ajusta al ancho real de la tarjeta
 *   (unidades de contenedor), con tope de 28 px, para que nunca se parta a mitad del número.
 * - Textos con palabras (p. ej. un nombre de cliente): pueden partirse en líneas, con letra más chica si son largos.
 */
function valueStyleFor(value: string | number): CSSProperties {
  const text = String(value);
  const isWords = /[A-Za-zÁÉÍÓÚÑáéíóúñ]{3,}\s+[A-Za-zÁÉÍÓÚÑáéíóúñ]{2,}/.test(text);
  if (isWords) {
    if (text.length > 26) return { fontSize: '16px', lineHeight: '1.25' };
    if (text.length > 14) return { fontSize: '18px', lineHeight: '1.25' };
    return {};
  }
  return { fontSize: `min(28px, calc(100cqw / ${Math.max(text.length, 1) * 0.64}))`, whiteSpace: 'nowrap' };
}

export function KpiCard({ label, value, sub, accent, tone, icon, iconBg, iconColor, delta, arrow, deltaTone }: Props) {
  const s = useStyles();
  const resolvedTone = tone ?? (accent ? 'danger' : 'default');

  return (
    <Card className={s.card}>
      {icon && (
        <div
          className={s.iconChip}
          style={{ background: iconBg ?? tokens.colorNeutralBackground3, color: iconColor ?? tokens.colorNeutralForeground2 }}
        >
          {icon}
        </div>
      )}
      <div className={s.label}>{label}</div>
      <div style={{ containerType: 'inline-size' }}>
        <div
          className={s.value}
          style={{
            color: resolvedTone === 'danger' ? tokens.colorStatusDangerForeground1 : tokens.colorNeutralForeground1,
            ...valueStyleFor(value),
          }}
        >
          {value}
        </div>
      </div>
      {sub && !delta && <div className={s.sub}>{sub}</div>}
      {delta && (
        <div className={s.deltaRow}>
          <span style={{ color: DELTA_TONE_COLOR[deltaTone ?? 'neutral'], fontWeight: 700 }}>
            {arrow ? `${arrow} ` : ''}{delta}
          </span>
          {sub && <span style={{ color: tokens.colorNeutralForeground3 }}>{sub}</span>}
        </div>
      )}
    </Card>
  );
}
